import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { updateMyProfile } from "@/services/profile";
import { ChoiceCard, OnboardingQuestion } from "./OnboardingQuestion";
import type { OnboardingStepProps } from "./types";

type Role = "player" | "supporter";

export default function RoleStep({
  profile,
  onValidityChange,
  registerSave,
  updateProfile,
}: OnboardingStepProps) {
  const [role, setRole] = useState<Role | null>(profile.onboardingRole ?? null);
  const roleRef = useRef(role);
  roleRef.current = role;

  useEffect(() => onValidityChange(role !== null), [onValidityChange, role]);
  useEffect(() => {
    registerSave(async () => {
      if (!roleRef.current) return false;
      try {
        await updateMyProfile({ onboardingRole: roleRef.current });
        updateProfile?.({ onboardingRole: roleRef.current });
        return true;
      } catch (error: any) {
        toast.error(String(error?.message || "Failed to save"));
        return false;
      }
    });
    return () => registerSave(null);
  }, [registerSave, updateProfile]);

  return (
    <OnboardingQuestion
      eyebrow="Let’s get started"
      title="Who are you setting this up for?"
      description="I’ll tailor the questions and language to match."
      coachCopy="Either way, we’ll build the plan around the golfer."
      coachBadge="01"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <ChoiceCard
          selected={role === "player"}
          onClick={() => setRole("player")}
          icon="ME"
          title="Myself"
          description="I’m the golfer going through recruiting"
        />
        <ChoiceCard
          selected={role === "supporter"}
          onClick={() => setRole("supporter")}
          icon="+1"
          title="A golfer I support"
          description="I’m a parent, guardian, coach, or advisor"
        />
      </div>
    </OnboardingQuestion>
  );
}
